import { ChangeDetectionStrategy, Component, Input, OnChanges, SimpleChanges, TemplateRef } from '@angular/core';
import { ScaleBand, ScaleLinear, ScalePoint } from 'd3-scale';
import type { ChartDataSeries, ChartLegendOptions } from '../../models/common-chart-models';
import { ChartDataEntry } from '../../models/common-chart-models';

@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'g[f-line-series]',
  templateUrl: './line-series.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class FireflyLineSeriesComponent implements OnChanges {
  @Input() xScale!: ScalePoint<string> | ScaleBand<string>;
  @Input() yScale!: ScaleLinear<number, number>;
  /** Optional right-axis scale. When provided, lines classified as dashed render against this scale. */
  @Input() lineYScale?: ScaleLinear<number, number>;
  @Input() data!: ChartDataSeries[];
  @Input() lineClasses!: string[];
  @Input() solidSeriesCount?: number;
  @Input() areaClasses!: string[];
  @Input() animationDuration = 500;
  @Input() animationDelay = 0;
  @Input() animation = true;
  @Input() curved = false;
  @Input() showArea = false;
  @Input() lineWidth = 2;
  @Input() circleRadius = 4;
  @Input() popoverOpenDelay = 200;
  @Input() popoverPlacement = 'top';
  @Input() popoverTriggers = 'manual';
  @Input() popoverTemplate!: TemplateRef<unknown> | null;
  @Input() legendOptions?: ChartLegendOptions;
  @Input() showPointerLine = false;
  @Input() areaIsSelected = false;
  /**
   * Inputs used for bar-line chart interactions, like animating circle-pointer movements.
   */
  @Input() activeDataPointId = -1;
  @Input() nearestXPointer!: number | undefined | null;

  lines!: ChartDataEntry[][];
  lineSeriesLabels: string[] = [];
  lineDashArrays: Array<string | null> = [];
  cachedPopoverData: { name: string; series: ChartDataEntry[] } | null = null;

  /** Recompute derived line/legend data whenever inputs change. */
  ngOnChanges(changes: SimpleChanges): void {
    if (changes.data || changes.lineClasses || changes.solidSeriesCount) {
      this.buildLines();
    }

    // Cache popover data to avoid allocating arrays/maps during template checks.
    if (changes.activeDataPointId || changes.legendOptions || changes.data) {
      this.cachedPopoverData = this.buildPopoverData();
    }
  }

  /** Build line series and labels from the input dataset. */
  private buildLines(): void {
    const lines: { [name: string]: ChartDataEntry[] } = {};
    const labels: string[] = [];

    this.data.forEach(d => {
      (d.series as ChartDataEntry[]).forEach(entry => {
        if (!lines[entry.name]) {
          lines[entry.name] = [];
          labels.push(entry.name);
        }
        lines[entry.name].push({
          name: d.name,
          value: entry.value
        });
      });
    });

    const classMap = this.getLineClassMap(labels);
    this.lines = labels.map(label =>
      (lines[label] || []).map(entry => ({
        ...entry,
        cssClass: classMap[label] ?? ''
      }))
    );
    this.lineSeriesLabels = labels;

    if (this.solidSeriesCount !== undefined && this.solidSeriesCount > this.lines.length) {
      console.warn(
        `[FireflyLineSeriesComponent] solidSeriesCount (${this.solidSeriesCount}) exceeds ` +
          `the total number of lines (${this.lines.length}). Dashed series will not render correctly.`
      );
    }

    this.lineDashArrays = this.lines.map((_, index) => {
      // Dashed rendering is determined exclusively by solidSeriesCount, which
      // is derived from dashedSeriesData.  lineClasses tokens no longer drive
      // stroke-dasharray so there is no class-based fallback here.
      if (this.solidSeriesCount !== undefined && index >= this.solidSeriesCount) return '6,6';
      return null;
    });
  }

  /** Build cached popover payload for the current active data point. */
  private buildPopoverData(): { name: string; series: ChartDataEntry[] } | null {
    if (this.activeDataPointId < 0 || !this.data?.length) return null;
    const name = this.data[this.activeDataPointId]?.name ?? '';
    const legendClassMap = this.getLegendClassMap();

    // Build a label→entry map for O(1) lookup when reordering.
    const entryByLabel = new Map<string, ChartDataEntry>();
    this.lines.forEach((line, index) => {
      const label = this.lineSeriesLabels[index];
      const entry = line[this.activeDataPointId];
      if (label && entry) {
        entryByLabel.set(label, {
          ...entry,
          name: label,
          cssClass: this.mergeCssClasses(entry.cssClass ?? '', legendClassMap[label] ?? '')
        });
      }
    });

    const legendData = this.legendOptions?.data ?? [];
    const orderedLabels: string[] = legendData.length
      ? [
          ...legendData.map(item => item.label).filter(l => entryByLabel.has(l)),
          ...this.lineSeriesLabels.filter(l => !legendData.some(item => item.label === l))
        ]
      : this.lineSeriesLabels;

    const series = orderedLabels
      .map(label => entryByLabel.get(label))
      .filter((e): e is ChartDataEntry => e !== undefined);

    return { name, series };
  }

  /** Map legend labels to marker classes for popover rendering. */
  private getLegendClassMap(): Record<string, string> {
    if (!this.legendOptions?.data?.length) return {};
    return this.legendOptions.data.reduce((acc, item) => {
      if (item.label && item.markerCssClass) {
        acc[item.label] = item.markerCssClass;
      }
      return acc;
    }, {} as Record<string, string>);
  }

  /** Build a map of series label -> css class from the lineClasses input. */
  private getLineClassMap(labels: string[]): Record<string, string> {
    const result: Record<string, string> = {};
    labels.forEach((label, index) => {
      const normalized = this.lineClasses?.[index] ?? '';
      if (normalized) {
        result[label] = normalized;
      }
    });
    return result;
  }

  /** Merge cssClass strings without duplicates. */
  private mergeCssClasses(base: string, extra: string): string {
    if (!base && !extra) return '';
    const tokens = `${base} ${extra}`.split(/\s+/).filter(Boolean);
    return [...new Set(tokens)].join(' ');
  }
}
